/**
 * API client configuration and utilities for Studio Bereikbaar Platform V4
 * Uses session-based authentication via the SB Keycloak OIDC broker
 * (Keycloak federates users to Microsoft Entra ID)
 */
import axios, { AxiosResponse } from 'axios'

// Create API client instance
// Note: VITE_API_URL is embedded at build time, fallback to relative path for production
export const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
  timeout: 10000,
  withCredentials: true, // Include cookies for session auth
  headers: {
    'Content-Type': 'application/json',
  },
})

// Response interceptor for error handling
api.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear any local user data
      localStorage.removeItem('sb_user')

      // Only redirect to login if not already there (prevents infinite loop)
      if (window.location.pathname !== '/login') {
        window.location.href = '/login'
      }
    }
    return Promise.reject(error)
  }
)

// Types for Microsoft authentication
export interface MicrosoftUser {
  id: string
  name: string
  email: string
  roles?: string[]
  login_time?: string
}

// API functions for Microsoft authentication
export const authAPI = {
  /**
   * Initiate Microsoft login (redirect to backend auth endpoint)
   */
  initiateLogin: (): void => {
    window.location.href = '/api/auth/login'
  },

  /**
   * Logout current user
   */
  logout: async (): Promise<void> => {
    try {
      await api.post('/auth/logout')
    } finally {
      localStorage.removeItem('sb_user')
      // Redirect to login after logout
      window.location.href = '/login'
    }
  },

  /**
   * Get current authenticated user from session
   */
  getCurrentUser: async (): Promise<MicrosoftUser> => {
    const response = await api.get('/auth/profile')
    return response.data.user || response.data
  },

  /**
   * Check if user is authenticated (has valid session)
   */
  checkAuth: async (): Promise<MicrosoftUser | null> => {
    try {
      const user = await authAPI.getCurrentUser()
      return user
    } catch (error) {
      return null
    }
  },
}

export const healthAPI = {
  /**
   * Check API health status
   */
  check: async () => {
    const response = await api.get('/health')
    return response.data
  },

  /**
   * Check backend health status
   */
  backend: async () => {
    const response = await api.get('/health')
    return response.data
  },
}

export const toolsAPI = {
  /**
   * Get available tools
   */
  getTools: async () => {
    const response = await api.get('/tools')
    return response.data
  },
}

// Export default API instance
export default api